import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Calendar, Eye, Tag, ArrowLeft, Share2 } from 'lucide-react';

const BlogPostPage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [relatedPosts, setRelatedPosts] = useState([]);

  const backendUrl = process.env.REACT_APP_BACKEND_URL || '';

  useEffect(() => {
    fetchPost();
    window.scrollTo(0, 0);
  }, [slug]);

  const fetchPost = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${backendUrl}/api/blog/${slug}`);
      if (res.ok) {
        const data = await res.json();
        setPost(data);
        
        // Fetch related posts if post has tags
        if (data.tags && data.tags.length > 0) {
          const relatedRes = await fetch(`${backendUrl}/api/blog?tag=${encodeURIComponent(data.tags[0])}&limit=3`);
          if (relatedRes.ok) {
            const relatedData = await relatedRes.json();
            setRelatedPosts(relatedData.filter(p => p.slug !== slug).slice(0, 2));
          }
        }
      } else {
        navigate('/blog');
      }
    } catch (error) {
      console.error('Error fetching blog post:', error);
      navigate('/blog');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleDateString('tr-TR', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: post.title,
          text: post.excerpt,
          url: window.location.href,
        });
      } catch (err) {
        console.log('Share cancelled');
      }
    } else {
      navigator.clipboard.writeText(window.location.href);
      alert('Bağlantı kopyalandı!');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen pt-32 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-black"></div>
      </div>
    );
  }

  if (!post) {
    return null;
  }

  return (
    <div className="min-h-screen pt-32 pb-20">
      <article className="max-w-4xl mx-auto px-6">
        {/* Back Link */}
        <Link 
          to="/blog" 
          className="inline-flex items-center gap-2 text-gray-500 hover:text-black mb-8 transition-colors"
        >
          <ArrowLeft size={18} />
          Blog'a Dön
        </Link>

        {/* Header */}
        <header className="mb-8">
          {/* Tags */}
          {post.tags && post.tags.length > 0 && (
            <div className="flex gap-2 mb-4">
              {post.tags.map((tag) => (
                <Link 
                  key={tag}
                  to={`/blog?tag=${encodeURIComponent(tag)}`}
                  className="text-xs text-gray-500 bg-gray-100 px-3 py-1 hover:bg-gray-200 transition-colors"
                >
                  {tag}
                </Link>
              ))}
            </div>
          )}
          
          {/* Title */}
          <h1 className="text-3xl md:text-4xl font-light tracking-wide mb-4">
            {post.title}
          </h1>
          
          {/* Meta */}
          <div className="flex items-center gap-6 text-sm text-gray-500">
            <div className="flex items-center gap-2">
              <Calendar size={16} />
              <span>{formatDate(post.published_at)}</span>
            </div>
            <div className="flex items-center gap-2">
              <Eye size={16} />
              <span>{post.view_count || 0} görüntüleme</span>
            </div>
            <button 
              onClick={handleShare}
              className="flex items-center gap-2 hover:text-black transition-colors"
            >
              <Share2 size={16} />
              <span>Paylaş</span>
            </button>
          </div>
        </header>

        {/* Cover Image */}
        {post.cover_image && (
          <div className="aspect-[16/9] mb-8 overflow-hidden bg-gray-100">
            <img
              src={post.cover_image}
              alt={post.title}
              className="w-full h-full object-cover"
            />
          </div>
        )}

        {/* Video */}
        {post.video_url && (
          <div className="mb-8">
            <video
              src={post.video_url}
              controls
              className="w-full rounded-lg"
              poster={post.cover_image}
            />
          </div>
        )}

        {/* Content */}
        <div 
          className="prose prose-lg max-w-none mb-12"
          style={{
            lineHeight: '1.8',
          }}
        >
          {/* Render content - supporting basic HTML or plain text */}
          <div 
            dangerouslySetInnerHTML={{ 
              __html: post.content.replace(/\n/g, '<br />') 
            }} 
          />
        </div>

        {/* Gallery Images */}
        {post.images && post.images.length > 0 && (
          <div className="mb-12">
            <h3 className="text-lg font-medium mb-4">Galeri</h3>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              {post.images.map((img, idx) => (
                <div key={idx} className="aspect-square overflow-hidden bg-gray-100">
                  <img
                    src={img}
                    alt={`${post.title} - ${idx + 1}`}
                    className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
                  />
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Divider */}
        <hr className="my-12 border-gray-200" />

        {/* Related Posts */}
        {relatedPosts.length > 0 && (
          <div>
            <h3 className="text-xl font-medium mb-6">İlgili Yazılar</h3>
            <div className="grid md:grid-cols-2 gap-6">
              {relatedPosts.map((relatedPost) => (
                <Link 
                  key={relatedPost.post_id}
                  to={`/blog/${relatedPost.slug}`}
                  className="group flex gap-4 p-4 bg-gray-50 hover:bg-gray-100 transition-colors"
                >
                  {relatedPost.cover_image && (
                    <div className="w-24 h-24 flex-shrink-0 overflow-hidden bg-gray-200">
                      <img
                        src={relatedPost.cover_image}
                        alt={relatedPost.title}
                        className="w-full h-full object-cover"
                      />
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <h4 className="font-medium group-hover:text-gray-600 transition-colors line-clamp-2">
                      {relatedPost.title}
                    </h4>
                    <p className="text-sm text-gray-500 mt-1 line-clamp-2">
                      {relatedPost.excerpt}
                    </p>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        )}
      </article>
    </div>
  );
};

export default BlogPostPage;
